import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Dashboard Report|CE Activity Import';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ComptiaCustomDashboardReports,
	portal: 'SP',
};

const htmlContent = `
<p>Press the Import CE Activities button, then select a CSV file filled with CE Activities to import to receive a CSV file with results, errors, etc.</p>

<p><strong>Warning! This tool does not protect against duplicate imports.</strong> A duplicate is defined as a record that matches to the same candidate account, 
CE Activity, and activity date. If duplicates are accidentally created, you will be responsible for finding and invalidating them, one at a time. The most 
common way to get duplicates is to upload the same file twice, or to take the downloaded results file and upload it back into the tool. There are no checks 
to prevent users from submitting uploads of duplicate activities. Please make sure the .csv file is correct and has not been previously uploaded prior to 
submitting the file.</p>

<strong>To create the Comma Separated File (CSV) input for the CE Activities:</strong>

<p>The following column headers are recognized by the parser:</p>
<ul>
<li>Candidate ID</li>
<li>Last Name</li>
<li>Activity Code</li>
<li>Activity Date</li>
<li>Activity Points</li>
<li>Activity Provider</li>
<li>Activity Comments</li>
</ul>

<p>All of the above fields are required. Any other field in the .csv file will cause the import to be rejected.</p>

<p><strong>Return CSV file:</strong><br />
After pressing the Import CE Activities button, a CSV file will be returned with following columns:</p>
<ul>
<li>Candidate ID</li>
<li>Last Name</li>
<li>Activity Code</li>
<li>Activity Date</li>
<li>Activity Points</li>
<li>Activity Provider</li>
<li>Activity Comments</li>
<li>Status</li>
</ul>

<p>The status field will contain one of these values:</p>
<ul>
<li>Candidate Merge - The candidate is part of a current merge, please retry them later.</li>
<li>Invalid Date - The Activity Date must be between 1990-01-01 and today.</li>
<li>Invalid Points - Points must be greater than zero.</li>
<li>No Match - Candidate not found.</li>
<li>Name Mismatch - Candidate last name does not match given Candidate ID.</li>
<li>Invalid Code - Activity Code does not exist.</li>
<li>Points auto-adjusted - CE Activity was imported, but the points were adjusted to match the CE catalog entry.</li>
<li>Success - CE Activity was imported.</li>
</ul>
`;

export type SPCEActivityImportLocNames =
	| 'SPCEActivityImportPageTitle'
	| 'SPCEActivityImportPageHeading'
	| 'SPCEActivityImportButton'
	| 'SPCEActivityImportInstructions'
	| 'SPCEActivityImportCsvUploadError'
	| 'SPCEActivityImportSuccess';

export const spCEActivityImportLocs: LocDefs<SPCEActivityImportLocNames> = {
	SPCEActivityImportPageTitle: {
		defaultValue: 'CE Activity Import',
		description: 'Page title for the CE activity import page',
		displayPath: `${dpRoot}|Page Title`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPCEActivityImportPageHeading: {
		defaultValue: 'CE Activity Import',
		description: 'Page Heading for the CE activity import page',
		displayPath: `${dpRoot}|Page Heading`,
		displayType: 'Title',
		...sharedProperties,
	},
	SPCEActivityImportButton: {
		defaultValue: 'Import CE Activities',
		description: 'Button text for the CE activity import section.',
		displayPath: `${dpRoot}|CE Activity Import Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	SPCEActivityImportInstructions: {
		defaultValue: htmlContent,
		description:
			'Instructions for the CE activity import section.',
		displayPath: `${dpRoot}|CE Activity Import Instructions`,
		displayType: 'Content',
		shared: false,
		htmlAllowed: true,
		feature: HubFeature.ComptiaCustomDashboardReports,
		portal: 'SP',
	},
	SPCEActivityImportCsvUploadError: {
		defaultValue: 'Error parsing CSV file.',
		description: 'Error message for the CSV upload error.',
		displayPath: `${dpRoot}|CSV Upload Error`,
		displayType: 'Error',
		...sharedProperties,
	},
	SPCEActivityImportSuccess: {
		defaultValue: 'CE Activities imported successfully. Your download should start shortly.',
		description: 'Success message when importing CE activities.',
		displayPath: `${dpRoot}|Verification Success`,
		displayType: 'Notification',
		...sharedProperties,
	},
};

export type SPCEActivityImportLocType = LocMap<SPCEActivityImportLocNames>;

export const CE_ACTIVITY_IMPORT_LOC_KEYS = MapLocKeys<SPCEActivityImportLocNames>(
	spCEActivityImportLocs
);
